'use client'
import { jsx } from 'react/jsx-runtime';
import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { dynamicEvents } from '../../events/dynamicEvents.js';

/** Context for accessing the current user/session's wallets */
const UserWalletsContext = createContext(undefined);
const UserWalletsProvider = ({ children }) => {
    const [userWallets, _setUserWallets] = useState([]);
    const prevUserWallets = useRef([]);
    const addedWalletsIds = useRef([]);
    const removedWalletsIds = useRef([]);
    const setUserWallets = useCallback((returnUpdatedWallets) => {
        _setUserWallets((prevWallets) => {
            const updatedWallets = returnUpdatedWallets(prevWallets);
            // Use old state value if nothing changed to prevent unnecessary re-renders
            if (updatedWallets.length === 0 && prevWallets.length === 0)
                return prevWallets;
            prevUserWallets.current = prevWallets;
            return updatedWallets;
        });
    }, []);
    // it's an useEffect because it needs to be called after the userWallets state is updated
    useEffect(() => {
        addedWalletsIds.current.forEach((walletId) => {
            const wallet = userWallets.find(({ id }) => id === walletId);
            if (!wallet)
                return;
            dynamicEvents.emit('walletAdded', wallet, userWallets);
        });
        removedWalletsIds.current.forEach((walletId) => {
            const wallet = prevUserWallets.current.find(({ id }) => id === walletId);
            if (!wallet)
                return;
            dynamicEvents.emit('walletRemoved', wallet, userWallets);
        });
        addedWalletsIds.current = [];
        removedWalletsIds.current = [];
    }, [userWallets]);
    const memoizedRegisterUserWallet = useCallback((newWallet) => setUserWallets((userWallets) => [...userWallets, newWallet]), [setUserWallets]);
    const memoizedRemoveUserWallet = useCallback((walletId) => setUserWallets((userWallets) => userWallets.filter(({ id }) => walletId !== id)), [setUserWallets]);
    return (jsx(UserWalletsContext.Provider, { value: {
            addedWalletsIds,
            registerUserWallet: memoizedRegisterUserWallet,
            removeUserWallet: memoizedRemoveUserWallet,
            removedWalletsIds,
            setUserWallets,
            userWallets,
        }, children: children }));
};
/** Provides access to state and setters of the current user/session wallets array */
const useInternalUserWallets = () => {
    const context = useContext(UserWalletsContext);
    if (!context)
        throw new Error('Can only call useInternalUserWallets inside UserWalletsProvider');
    return context;
};
/** Provides access to the current user/session wallets */
const useUserWallets = () => {
    const context = useContext(UserWalletsContext);
    if (!context)
        throw new Error('Can only call useUserWallets inside UserWalletsProvider');
    return context.userWallets;
};

export { UserWalletsContext, UserWalletsProvider, useInternalUserWallets, useUserWallets };
